import React, { useEffect, useRef, useState } from 'react'
import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { useAPI } from '../../../Contexts/APIContext'
import { useInView } from 'react-intersection-observer'
import Loader from '../../GeneralComponents/Loader'
import CollectionFeelsModal from '../CollectionFeelsModal/CollectionFeelsModal'
import SearchInput from '../../DashboardComponents/SearchInput/SearchInput'
import { extractFeelId, isFeelId } from '../../../Helpers/feelUtils'
import PropTypes from 'prop-types'
import classes from './index.module.css'

export default function CollectionModal({
  closeModal,
  setToNewCollection,
  reservedFeels,
}) {
  const section = ''
  const pageLimit = 10
  const { searchFeels, getFeels, getFeelData } = useAPI()
  const [ref, inView] = useInView()
  const [isOpen, setIsOpen] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [searchTimer, setSearchTimer] = useState(null)
  const [directFeelId, setDirectFeelId] = useState(null)

  // Query for direct feel by ID
  const directFeelQuery = useQuery({
    queryKey: ['direct-feel', directFeelId],
    queryFn: () => getFeelData(directFeelId),
    enabled: !!directFeelId,
  })

  // Query for search results
  const {
    data,
    isError,
    error,
    isLoading,
    fetchNextPage,
    isFetching,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['collection-add-feels', searchTerm],
    queryFn: ({ pageParam = 1 }) =>
      searchTerm === ''
        ? getFeels(section, pageParam, pageLimit)
        : searchFeels(searchTerm, pageParam, pageLimit, undefined, '', true),
    getNextPageParam: pageData => {
      const nextPage = pageData.p + 1
      const newPage =
        pageData.p * pageLimit < pageData.feels_count ? nextPage : undefined
      return newPage
    },
    enabled: !directFeelId, // Don't search when we have a direct feel ID
  })

  useEffect(() => {
    if (inView) {
      fetchNextPage()
    }
  }, [inView])

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'auto'
    }
    return () => {
      document.body.style.overflow = 'auto'
    }
  }, [isOpen])

  const handleCloseClick = () => {
    setIsOpen(false)
  }

  function handleSearchInput(e) {
    if (searchTimer !== null) {
      clearTimeout(searchTimer)
      setSearchTimer(null)
    }
    
    const inputValue = e.target.value
    const feelId = extractFeelId(inputValue)
    
    if (feelId) {
      // If it's a feel ID, use direct fetch
      setDirectFeelId(feelId)
      setSearchTerm('')
    } else {
      // Otherwise, use regular search
      setDirectFeelId(null)
      setSearchTimer(
        setTimeout(() => {
          setSearchTerm(inputValue)
          setSearchTimer(null)
        }, 500)
      )
    }
  }

  return (
    <>
      {isOpen && (
        <div
          className={classes.bgContainer}
          onClick={() => {
            closeModal()
          }}
        >
          <div className={classes.container} onClick={e => e.stopPropagation()}>
            <div>
              <SearchInput
                fullWidth
                placeholder={'Search'}
                onSearchInput={handleSearchInput}
              />
            </div>
            <div className={classes.feelsWrapper}>
              {/* Direct feel result */}
              {directFeelQuery.data && (
                <div key={directFeelQuery.data.f_id}>
                  <CollectionFeelsModal
                    feel={directFeelQuery.data}
                    setToNewCollection={setToNewCollection}
                    reservedFeels={reservedFeels}
                    closeModal={closeModal}
                  />
                </div>
              )}
              
              {/* Search results */}
              {!directFeelId && data &&
                data.pages.map((page, index) => (
                  <React.Fragment key={index}>
                    {page.feels.map((feel, innerIndex) => (
                      <div key={feel.f_id} ref={ref}>
                        <CollectionFeelsModal
                          feel={feel}
                          setToNewCollection={setToNewCollection}
                          reservedFeels={reservedFeels}
                          closeModal={closeModal}
                        />
                      </div>
                    ))}
                  </React.Fragment>
                ))}
            </div>
            {!isError && !directFeelQuery.isError && (
              <Loader isLoading={isLoading || isFetchingNextPage || directFeelQuery.isLoading} />
            )}
            {(isError || directFeelQuery.isError) && (
              <h1 className={classes.errorText}>
                {'Oops, something went wrong'}
              </h1>
            )}

            {/* <Loader isLoading={isLoading || isFetchingNextPage} /> */}
          </div>
        </div>
      )}
    </>
  )
}

CollectionModal.propTypes = {
  closeModal: PropTypes.func.isRequired,
  setToNewCollection: PropTypes.func,
  reservedFeels: PropTypes.arrayOf(PropTypes.string),
}
